import { useCallback } from 'react';

import { SubmitHandler } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { routeTemplateActions } from '../routeTemplates.slice';
import { RouteTemplateFormValues } from './types';
import { useUpsertRouteTemplate } from './useUpsertRouteTemplate';

export function useSubmitRouteTemplateForm(
  isEditing: boolean,
  currentTripTemplateIds: string[],
  currentMultiloadTripTemplateIds: string[],
  onSubmit: () => void,
  onError: () => void
) {
  const dispatch = useAppDispatch();
  const assignments = useAppSelector(
    (state) => state.routeTemplates.bulkCreationAssignments
  );
  const selectedAssignment = useAppSelector(
    (state) => state.routeTemplates.selectedAssignment
  );

  const { createRouteTemplate, updateRouteTemplate } = useUpsertRouteTemplate();

  const saveAndClose = useCallback<SubmitHandler<RouteTemplateFormValues>>(
    async (data) => {
      try {
        if (isEditing) {
          await updateRouteTemplate(
            data,
            currentTripTemplateIds,
            currentMultiloadTripTemplateIds
          );
        } else {
          await createRouteTemplate(data);
        }

        dispatch(
          show({
            type: 'success',
            title: isEditing
              ? 'Successfully updated route template'
              : 'Successfully created route template',
          })
        );
        onSubmit();
        dispatch(routeTemplateActions.setShowRouteTemplateForm(false));
        dispatch(routeTemplateActions.resetSelectedTrips());
        dispatch(routeTemplateActions.setWorkflow('single'));
        dispatch(routeTemplateActions.selectAssignment(undefined));
      } catch (e) {
        onError();
        dispatch(
          show({
            type: 'error',
            title: isEditing
              ? 'Failed to update route template'
              : 'Failed to create route template',
            description: formatServerError(e),
          })
        );
      }
    },
    [
      isEditing,
      dispatch,
      onSubmit,
      updateRouteTemplate,
      createRouteTemplate,
      onError,
      currentTripTemplateIds,
      currentMultiloadTripTemplateIds,
    ]
  );

  const saveAndNext = useCallback<SubmitHandler<RouteTemplateFormValues>>(
    async (data) => {
      try {
        await createRouteTemplate(data);
        dispatch(
          show({
            type: 'success',
            title: 'Successfully created route template',
          })
        );
        const nextAssignments = assignments.filter(
          (assignment) => assignment.id !== selectedAssignment?.id
        );
        dispatch(
          routeTemplateActions.setBulkCreationAssignments(nextAssignments)
        );
        dispatch(routeTemplateActions.resetSelectedTrips());
      } catch (e) {
        onError();
        dispatch(
          show({
            type: 'error',
            title: 'Failed to create route template',
            description: formatServerError(e),
          })
        );
      }
    },
    [
      assignments,
      createRouteTemplate,
      dispatch,
      onError,
      selectedAssignment?.id,
    ]
  );

  return {
    saveAndClose,
    saveAndNext,
  };
}
